/**
 * Animated Image Gallery.
 *
 * The images never move. Each keeps its position; what changes is the SIZE
 * assigned to that position. `sizes[column]` holds a size id — 0 large, 1
 * small, 2 medium — so clicking a column swaps its size with whichever column
 * currently holds the large one. The clicked image grows in place, and the
 * featured one shrinks into the size just given up.
 *
 * Sizes are published as a width FRACTION per item rather than as a
 * grid-template-columns track list. A track list can only be interpolated when
 * the track count matches, and it can't be registered with @property, so the
 * resize couldn't be animated. A per-item number can be registered (see
 * style.css) and animates cleanly.
 */
window.AnimatedImageGallery = (count = 3, canSwap = true) => {
	// Design widths for the three slots — 456 / 195 / 310, all 6:5. Held as
	// ratios, not pixels, so the arrangement scales with its container.
	const TRACKS = [456, 195, 310];

	// Stacked equivalents. The design keeps the size hierarchy on mobile
	// rather than letting all three go full width.
	const WIDTHS = ['100%', '54%', '80%'];

	const total = Number(count) || 0;

	return {
		sizes: Array.from({ length: total }, (_, i) => i),
		canSwap: Boolean(canSwap) && total > 1,

		/** Sum of the tracks actually in play — the block may hold 1–3 images. */
		get trackTotal() {
			return this.sizes.reduce((sum, size) => sum + TRACKS[size], 0);
		},

		/**
		 * Share of the row this column takes, as a 0–1 fraction. CSS turns it
		 * into a width once the gaps are subtracted.
		 */
		fractionAt(column) {
			return (TRACKS[this.sizes[column]] / this.trackTotal).toFixed(5);
		},

		/** Stacked-layout width for the image sitting in this column. */
		widthAt(column) {
			return WIDTHS[this.sizes[column]] || '100%';
		},

		isLarge(column) {
			return this.sizes[column] === 0;
		},

		/**
		 * Grow the image in `column` to the large size, shrinking the current
		 * large one into the size this column gives up.
		 */
		promote(column) {
			if (!this.canSwap || this.isLarge(column)) return;

			const featured = this.sizes.indexOf(0);
			const swap = this.sizes[column];

			this.sizes[column] = 0;
			this.sizes[featured] = swap;
		},

		/**
		 * Announced by the block's live region. Screen reader users get no
		 * information from a size change on its own, so state it in words.
		 */
		get status() {
			const featured = this.sizes.indexOf(0);
			return `Image ${featured + 1} of ${total} is now featured.`;
		},
	};
};
